'use client'

import { useEffect, useRef, useState } from 'react'
import Section from 'components/Section'
import { Typography, MediaImageStandard } from 'components/Contentful'

// Desktop carousel fixed dimensions (px)
const CENTER_W = 500
const CENTER_H = 333
const SQUARE_SIZE = 250 // 1:1, 250x250
const GAP = 24

const createImageContent = (src) => ({
  fields: {
    file: {
      url: src,
      fileName: 'hero-json-pro-about-image',
    },
  },
})

/** Returns { width, height } for a carousel slot at `dist` positions from center */
function getSlotDimensions(dist) {
  if (dist === 0) return { width: CENTER_W, height: CENTER_H }
  if (dist === 1) return { width: SQUARE_SIZE, height: SQUARE_SIZE }
  return { width: CENTER_W, height: CENTER_H } // peek — same ratio as center
}

function HeroJsonProAbout(content) {
  const jsonContent =
    content?.content?.fields?.sectionContents?.find(
      (section) => section?.fields?.uiComponent === 'JsonContent'
    )?.fields?.jsonContent ||
    content?.content?.jsonContent ||
    content?.jsonContent

  // Hooks must be declared before any early return
  const [step, setStep] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [hasInteracted, setHasInteracted] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const onInteract = () => setHasInteracted(true)
    window.addEventListener('mousemove', onInteract, { once: true })
    window.addEventListener('touchstart', onInteract, { once: true })
    return () => {
      window.removeEventListener('mousemove', onInteract)
      window.removeEventListener('touchstart', onInteract)
    }
  }, [])

  useEffect(() => {
    const update = () => setViewportWidth(document.documentElement.clientWidth)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const imageList = Array.isArray(jsonContent?.images) ? jsonContent.images : []

  useEffect(() => {
    if (imageList.length <= 1 || !hasInteracted) return
    const id = setInterval(() => setStep((s) => s + 1), 3000)
    return () => clearInterval(id)
  }, [imageList.length, hasInteracted])

  if (!jsonContent) return null

  const { title, subtitle } = jsonContent

  const n = imageList.length

  /**
   * Computes the CSS `left` value (px) for a slot at `offset` positions from
   * the active item (-2…+2). Offset 0 is always centered in the container.
   */
  const getItemLeft = (offset) => {
    if (!containerWidth) return 0
    const activeLeft = containerWidth / 2 - CENTER_W / 2

    if (offset === 0) return activeLeft

    if (offset > 0) {
      let left = activeLeft + CENTER_W + GAP
      for (let j = 1; j < offset; j++) {
        left += getSlotDimensions(j).width + GAP
      }
      return left
    }

    // offset < 0 — walk leftward from the active item
    let right = activeLeft - GAP
    for (let j = -1; j > offset; j--) {
      right -= getSlotDimensions(-j).width + GAP
    }
    return right - getSlotDimensions(-offset).width
  }

  return (
    <Section disableGutters className="">
      <div className="grid grid-cols-4 breakpointMd:grid-cols-6 breakpointLg:grid-cols-12 gap-4 breakpointMd:gap-6 breakpointLg:gap-8 pt-[48px] pb-[48px] breakpointLg:pt-[80px] breakpointLg:pb-[80px]">
        {title && (
          <Typography
            component="h1"
            content={title}
            className="col-span-full breakpointLg:col-start-4 breakpointLg:col-end-10 font-ultramarine text-[40px] font-bold leading-[130%] breakpointLg:text-[72px] breakpointLg:leading-[120%] text-[#1D252F] text-left breakpointMd:text-center px-[20px] breakpointMd:px-[32px] breakpointLg:px-0"
          />
        )}
        {subtitle && (
          <Typography
            content={subtitle}
            className="col-span-full breakpointLg:col-start-4 breakpointLg:col-end-10 font-ultramarine text-[16px] font-normal leading-[140%] breakpointLg:text-[18px] text-[#1D252F] text-left breakpointMd:text-center px-[20px] breakpointMd:px-[32px] breakpointLg:px-0"
          />
        )}

        {imageList.length > 0 && (
          <>
            {/* ── Mobile: animated carousel ── */}
            <div
              className="col-span-full breakpointMd:hidden relative overflow-hidden"
              style={{ height: '200px' }}
            >
              {Array.from({ length: 7 }, (_, k) => step - 3 + k).map((v) => {
                const offset = v - step
                const dist = Math.abs(offset)
                const imageIndex = ((v % n) + n) % n
                const src = imageList[imageIndex]
                const mDist = Math.min(dist, 2)
                const width = mDist === 0 ? 300 : mDist === 1 ? 175 : 300
                const height = mDist === 0 ? 200 : mDist === 1 ? 175 : 200
                const getMobileLeft = (off) => {
                  const base = 20
                  if (off === 0) return base
                  if (off > 0) {
                    let l = base + 300 + 16
                    for (let j = 1; j < off; j++) l += (j === 1 ? 175 : 300) + 16
                    return l
                  }
                  let r = base - 16
                  for (let j = -1; j > off; j--) r -= (Math.abs(j) === 1 ? 175 : 300) + 16
                  return r - (Math.abs(off) === 1 ? 175 : 300)
                }
                return (
                  <div
                    key={v}
                    className="absolute top-1/2 -translate-y-1/2 overflow-hidden rounded-[16px] transition-all duration-500 ease-in-out"
                    style={{
                      left: `${getMobileLeft(offset)}px`,
                      width: `${width}px`,
                      height: `${height}px`,
                      opacity: dist <= 2 ? 1 : 0,
                    }}
                  >
                    <MediaImageStandard
                      content={createImageContent(src)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )
              })}
            </div>

            {/* ── Desktop: peek | square | center | square | peek ── */}
            <div
              ref={containerRef}
              className="col-span-full hidden breakpointMd:block relative overflow-hidden"
              style={{
                height: `${CENTER_H}px`,
                ...(viewportWidth > 0
                  ? {
                      width: viewportWidth,
                      marginLeft: `calc(50% - ${viewportWidth / 2}px)`,
                    }
                  : {}),
              }}
            >
              {containerWidth > 0 &&
                Array.from({ length: 7 }, (_, k) => step - 3 + k).map((v) => {
                  const offset = v - step
                  const dist = Math.abs(offset)
                  const imageIndex = ((v % n) + n) % n
                  const src = imageList[imageIndex]
                  const { width, height } = getSlotDimensions(Math.min(dist, 2))
                  return (
                    <div
                      key={v}
                      className="absolute top-1/2 -translate-y-1/2 overflow-hidden rounded-[16px] transition-all duration-500 ease-in-out"
                      style={{
                        left: `${getItemLeft(offset)}px`,
                        width: `${width}px`,
                        height: `${height}px`,
                        opacity: dist <= 2 ? 1 : 0,
                      }}
                    >
                      <MediaImageStandard
                        content={createImageContent(src)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )
                })}
            </div>
          </>
        )}
      </div>
    </Section>
  )
}

export default HeroJsonProAbout
